'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import imageSizes from '@/data/imageSizes.json';

const PHOTO_SIZES = imageSizes as unknown as Record<string, [number, number]>;
import Breadcrumbs from '@/components/ui/Breadcrumbs';
import ImagePlaceholder from '@/components/ui/ImagePlaceholder';
import CTAPrimary from '@/components/ui/CTAPrimary';
import ScrollReveal from '@/app/components/ScrollReveal';
import { COMPANY } from '@/data/company';
import { PROJECT_PHOTOS } from '@/data/projectPhotos';
import { GUBBI_GUDU, PROJECT_STORIES } from '@/data/projectStories';
import { trackEvent } from '@/lib/analytics';

// ─── PROJECT DATA ─────────────────────────────────────────────────────────────
// Replace with real Willow project data when available.

interface Project {
  id: string;
  name: string | null;
  slug: string;
  location: string | null;
  propertyType: string | null;
  year: string | null;
  heroImage: string | null;
  heroImageAlt: string;
  featured: boolean;
}

const PROJECT_LIST: Project[] = [
  {
    id: 'p1',
    name: GUBBI_GUDU.name,
    slug: GUBBI_GUDU.slug,
    location: 'Anjanapura, Bangalore',
    propertyType: 'Villa · G+4',
    year: null,
    heroImage: '/assets/images/projects/project-1/gubbi-gudu-villa-living-room-with-walnut-tv-unit-and-fluted-wood-wall.jpg',
    heroImageAlt: 'Living room with brown and white interiors — warm tones, custom joinery and curated lighting',
    featured: true,
  },
  {
    id: 'p2',
    name: null,
    slug: 'rasha-apartment-hosa-road-electronic-city',
    location: null,
    propertyType: null,
    year: null,
    heroImage: null,
    heroImageAlt: 'REAL WILLOW PROJECT IMAGE REQUIRED',
    featured: false,
  },
  {
    id: 'p3',
    name: null,
    slug: 'saral-mane-g2-house-kanakapura-road',
    location: null,
    propertyType: null,
    year: null,
    heroImage: null,
    heroImageAlt: 'REAL WILLOW PROJECT IMAGE REQUIRED',
    featured: false,
  },
  {
    id: 'p4',
    name: null,
    slug: 'blue-on-mars-apartment-banashankari',
    location: null,
    propertyType: null,
    year: null,
    heroImage: null,
    heroImageAlt: 'REAL WILLOW PROJECT IMAGE REQUIRED',
    featured: false,
  },
  {
    id: 'p5',
    name: null,
    slug: 'the-green-storey-g3-house-bannerghatta',
    location: null,
    propertyType: null,
    year: null,
    heroImage: null,
    heroImageAlt: 'REAL WILLOW PROJECT IMAGE REQUIRED',
    featured: false,
  },
  {
    id: 'p6',
    name: null,
    slug: 'sage-and-sunshine-apartment-sarjapur',
    location: null,
    propertyType: null,
    year: null,
    heroImage: null,
    heroImageAlt: 'REAL WILLOW PROJECT IMAGE REQUIRED',
    featured: false,
  },
];

// Card images come from the same real-photo data as the project pages (one source of truth)
// Card images come from the photo data, and names/places from the written story (when one exists)
const PROJECTS: Project[] = PROJECT_LIST.map((p) => {
  const hero = PROJECT_PHOTOS[p.slug]?.hero;
  const story = PROJECT_STORIES[p.slug];
  const withPhoto = hero ? { ...p, heroImage: hero.src, heroImageAlt: hero.alt } : p;
  return story
    ? { ...withPhoto, name: story.name, location: story.location, propertyType: story.propertyType.split(' · ').slice(0, 2).join(' · ') }
    : withPhoto;
});

// Filter categories — update when real project types are confirmed
const CATEGORIES = ['All', 'Residential', 'Kitchen', 'Wardrobe', 'Full Home'];

// ─── HELPERS ──────────────────────────────────────────────────────────────────

function ProjectImage({
  project,
  aspectRatio = '3/4',
  priority = false,
}: {
  project: Project;
  aspectRatio?: string;
  priority?: boolean;
}) {
  if (project.heroImage) {
    return (
      PHOTO_SIZES[project.heroImage] ? (
        <Image
          src={project.heroImage}
          alt={project.heroImageAlt ?? ''}
          width={PHOTO_SIZES[project.heroImage][0]}
          height={PHOTO_SIZES[project.heroImage][1]}
          priority={priority}
          sizes="(min-width: 1024px) 40vw, 100vw"
          className="w-full h-full object-cover"
          style={{ aspectRatio: aspectRatio.replace('/', ' / '), display: 'block' }}
        />
      ) : (
        <img src={project.heroImage} alt={project.heroImageAlt} loading={priority ? 'eager' : 'lazy'} className="w-full h-full object-cover" style={{ aspectRatio: aspectRatio.replace('/', ' / '), display: 'block' }} />
      )
    );
  }
  return (
    <ImagePlaceholder
      aspectRatio={aspectRatio}
      label="REAL WILLOW IMAGE REQUIRED"
      className="w-full"
    />
  );
}

function ProjectMeta({ project }: { project: Project }) {
  const hasAny = project.propertyType || project.location || project.year;
  if (!hasAny) {
    return (
      <p className="text-caption italic opacity-40 mt-2">[Project details required]</p>
    );
  }
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-2">
      {project.propertyType && (
        <span className="text-section-label">{project.propertyType}</span>
      )}
      {project.propertyType && project.location && (
        <span className="text-caption opacity-30" aria-hidden="true">·</span>
      )}
      {project.location && (
        <span className="text-caption">{project.location}</span>
      )}
      {project.year && (
        <>
          <span className="text-caption opacity-30" aria-hidden="true">·</span>
          <span className="text-caption">{project.year}</span>
        </>
      )}
    </div>
  );
}

// ─── PAGE ─────────────────────────────────────────────────────────────────────

// `journey`: the next-visual cards for this page, built on the server (see src/data/journeyItems.ts)
export default function ProjectsContent({ journey }: { journey?: React.ReactNode } = {}) {
  const [activeCategory, setActiveCategory] = useState('All');

  const featuredProject = PROJECTS.find((p) => p.featured) ?? PROJECTS[0];
  const supportingProjects = PROJECTS.filter((p) => !p.featured);

  const handleProjectClick = (project: Project) => {
    trackEvent('project_view', {
      project_id: project.id,
      project_name: project.name ?? 'placeholder',
    });
  };

  return (
    <div className="bg-warm-white min-h-screen">

      {/* ── BREADCRUMBS ─────────────────────────────────────────── */}
      <div className="container-willow pt-8 pb-0">
        <Breadcrumbs
          items={[
            { label: 'Home', href: '/' },
            { label: 'Projects' },
          ]}
        />
      </div>

      {/* ── PAGE HEADER ─────────────────────────────────────────── */}
      <header className="container-willow pt-10 pb-14 md:pb-20">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 md:gap-16">
          {/* Left: Title */}
          <div className="max-w-xl">
            <h1
              className="text-hero-xl text-foreground"
              style={{ letterSpacing: '-0.035em', lineHeight: '1.05' }}
            >
              {/* Topic line inside the H1 so the heading says what the page is about; slogan below */}
              <span className="flex items-center gap-3 mb-5 leading-normal">
                <span className="block h-px w-8 flex-shrink-0" style={{ backgroundColor: 'var(--primary)' }} aria-hidden="true" />
                <span className="text-section-label">Interior Design Projects in Bangalore</span>
              </span>
              A Curated Body<br />
              <span style={{ color: 'var(--primary)' }}>of Work.</span>
            </h1>
          </div>

          {/* Right: Intro */}
          <div className="max-w-sm">
            <p className="text-body-lg">
              Interiors designed, manufactured and installed by Willow — in Bengaluru.
            </p>
            <p className="text-caption mt-3 leading-relaxed">
              Each project begins with understanding how the space will actually be used, then designing and building it to last.
            </p>
            <p className="text-caption mt-3 leading-relaxed">
              Building a house? See our{' '}
              <Link href="/independent-house-interiors-bangalore" className="font-semibold text-primary hover:underline">
                interiors for independent houses
              </Link>
              . Looking for ideas? Browse{' '}
              <Link href="/ideas" className="font-semibold text-primary hover:underline">
                ideas from these homes, with real costs
              </Link>
              .
            </p>
          </div>
        </div>
      </header>

      {/* ── CATEGORY FILTER ─────────────────────────────────────── */}
      <div className="border-t border-b border-border">
        <div className="container-willow">
          <div
            className="flex items-center overflow-x-auto"
            role="tablist"
            aria-label="Filter projects by category"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          >
            {CATEGORIES.map((cat) => (
              <button
                key={cat}
                role="tab"
                aria-selected={activeCategory === cat}
                onClick={() => setActiveCategory(cat)}
                className="flex-shrink-0 px-5 py-4 text-xs font-semibold tracking-widest uppercase transition-colors duration-150 border-b-2 -mb-px"
                style={{
                  color: activeCategory === cat ? 'var(--primary)' : 'var(--muted-foreground)',
                  borderBottomColor: activeCategory === cat ? 'var(--primary)' : 'transparent',
                  background: 'transparent',
                }}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* ── FEATURED PROJECT ────────────────────────────────────── */}
      <section
        className="container-willow pt-14 md:pt-20 pb-12 md:pb-16"
        aria-label="Featured project"
      >
        <ScrollReveal>
          <Link
            href={`/projects/${featuredProject.slug}`}
            className="group block"
            onClick={() => handleProjectClick(featuredProject)}
            aria-label={
              featuredProject.name
                ? `View project: ${featuredProject.name}`
                : 'View featured project'
            }
          >
            <div className="grid lg:grid-cols-[1.4fr_1fr] items-stretch">
              {/* Large image */}
              <div className="overflow-hidden" style={{ aspectRatio: '16 / 10' }}>
                <div
                  className="w-full h-full transition-transform duration-700"
                  style={{ transitionTimingFunction: 'cubic-bezier(0.16, 1, 0.3, 1)' }}
                >
                  <div className="group-hover:scale-[1.03] transition-transform duration-700 w-full h-full" style={{ transitionTimingFunction: 'cubic-bezier(0.16, 1, 0.3, 1)' }}>
                    <ProjectImage
                      project={featuredProject}
                      aspectRatio="16/10"
                      priority
                    />
                  </div>
                </div>
              </div>

              {/* Info panel */}
              <div
                className="flex flex-col justify-end p-8 md:p-12 lg:p-14"
                style={{ backgroundColor: 'var(--stone)' }}
              >
                <div className="flex items-center gap-2 mb-8">
                  <span
                    className="block h-px w-5 flex-shrink-0"
                    style={{ backgroundColor: 'var(--primary)' }}
                    aria-hidden="true"
                  />
                  <span className="text-section-label">FEATURED PROJECT</span>
                </div>

                <div className="mb-4">
                  {featuredProject.name ? (
                    <h2
                      className="text-display text-foreground group-hover:text-primary transition-colors duration-300"
                      style={{ lineHeight: '1.1' }}
                    >
                      {featuredProject.name}
                    </h2>
                  ) : (
                    <div>
                      <p className="text-xs font-medium tracking-widest uppercase opacity-40 mb-3">
                        [Project name required]
                      </p>
                      <div
                        className="h-7 opacity-20 rounded-sm"
                        style={{ backgroundColor: 'var(--border)', width: '72%' }}
                        aria-hidden="true"
                      />
                    </div>
                  )}
                </div>

                <ProjectMeta project={featuredProject} />

                <div className="mt-10 flex items-center gap-3">
                  <span
                    className="text-sm font-semibold tracking-wide"
                    style={{ color: 'var(--primary)' }}
                  >
                    View Project
                  </span>
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 20 20"
                    fill="none"
                    aria-hidden="true"
                    className="transition-transform duration-300 group-hover:translate-x-1.5"
                    style={{ color: 'var(--primary)' }}
                  >
                    <path
                      d="M4 10h12M10 4l6 6-6 6"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
              </div>
            </div>
          </Link>
        </ScrollReveal>
      </section>

      {/* ── SUPPORTING PROJECTS — ASYMMETRIC GRID ───────────────── */}
      <section
        className="container-willow pb-20 md:pb-28"
        aria-label="All projects"
      >
        {/*
          Asymmetric 12-column grid:
          Row 1: 7 + 5
          Row 2: 5 + 7
          Row 3: 6 + 6
        */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-x-6 gap-y-14 md:gap-y-20">
          {supportingProjects.map((project, index) => {
            const configs = [
              { span: 'lg:col-span-7', aspect: '4/3' },
              { span: 'lg:col-span-5', aspect: '3/4' },
              { span: 'lg:col-span-5', aspect: '3/4' },
              { span: 'lg:col-span-7', aspect: '4/3' },
              { span: 'lg:col-span-6', aspect: '4/3' },
              { span: 'lg:col-span-6', aspect: '4/3' },
            ];
            const cfg = configs[index % configs.length];

            return (
              <ScrollReveal
                key={project.id}
                className={`col-span-1 md:col-span-1 ${cfg.span}`}
              >
                <Link
                  href={`/projects/${project.slug}`}
                  className="group block"
                  onClick={() => handleProjectClick(project)}
                  aria-label={
                    project.name ? `View project: ${project.name}` : 'View project'
                  }
                >
                  {/* Image */}
                  <div className="overflow-hidden">
                    <div
                      className="group-hover:scale-[1.04] transition-transform duration-700"
                      style={{ transitionTimingFunction: 'cubic-bezier(0.16, 1, 0.3, 1)' }}
                    >
                      <ProjectImage project={project} aspectRatio={cfg.aspect} />
                    </div>
                  </div>

                  {/* Meta */}
                  <div className="mt-4">
                    {project.name ? (
                      <h3
                        className="text-base font-semibold text-foreground group-hover:text-primary transition-colors duration-200"
                        style={{ letterSpacing: '-0.01em' }}
                      >
                        {project.name}
                      </h3>
                    ) : (
                      <p className="text-sm font-medium opacity-40 italic">
                        [Project name required]
                      </p>
                    )}
                    <ProjectMeta project={project} />
                    <div className="mt-3 flex items-center gap-2">
                      <span
                        className="text-xs font-semibold tracking-wide"
                        style={{ color: 'var(--primary)' }}
                      >
                        View Project
                      </span>
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 20 20"
                        fill="none"
                        aria-hidden="true"
                        className="transition-transform duration-300 group-hover:translate-x-1.5"
                        style={{ color: 'var(--primary)' }}
                      >
                        <path
                          d="M4 10h12M10 4l6 6-6 6"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </div>
                  </div>
                </Link>
              </ScrollReveal>
            );
          })}
        </div>

      </section>

      {/* ── DIVIDER ─────────────────────────────────────────────── */}
      <div className="divider-willow" />

      {/* ── NEXT VISUALS — where to look next (visual journey) ── */}
      {journey}

      {/* ── START YOUR PROJECT CTA ──────────────────────────────── */}
      <section
        className="py-20 md:py-28"
        style={{ backgroundColor: 'var(--foreground)' }}
        aria-labelledby="projects-cta-heading"
      >
        <ScrollReveal>
          <div className="container-willow">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-10 md:gap-16">
              {/* Left */}
              <div className="max-w-lg">
                <div className="flex items-center gap-3 mb-6">
                  <span
                    className="block h-px w-6 flex-shrink-0"
                    style={{ backgroundColor: 'var(--primary)' }}
                    aria-hidden="true"
                  />
                  <span className="text-section-label">START YOUR PROJECT</span>
                </div>
                <h2
                  id="projects-cta-heading"
                  className="text-display"
                  style={{
                    color: 'var(--warm-white)',
                    lineHeight: '1.1',
                    letterSpacing: '-0.025em',
                  }}
                >
                  Planning Your Home Interiors in Bangalore?
                </h2>
                <p
                  className="mt-5 text-body-lg"
                  style={{ color: '#9E9992' }}
                >
                  Tell us what you&apos;re planning. We&apos;ll start by understanding the project.
                </p>
              </div>

              {/* Right: CTAs */}
              <div className="flex flex-col sm:flex-row gap-3 flex-shrink-0">
                <CTAPrimary label="START YOUR PROJECT" href="/contact" />
                <a
                  href={COMPANY.whatsapp.message}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => trackEvent('whatsapp_click', { page: '/projects' })}
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 text-sm font-semibold tracking-wide border transition-colors duration-150"
                  style={{ color: '#9E9992', borderColor: '#2A2825' }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.color = '#25D366';
                    e.currentTarget.style.borderColor = '#25D366';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.color = '#9E9992';
                    e.currentTarget.style.borderColor = '#2A2825';
                  }}
                  aria-label="Chat with Willow on WhatsApp"
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                    aria-hidden="true"
                  >
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                  </svg>
                  WHATSAPP WILLOW
                </a>
              </div>
            </div>
          </div>
        </ScrollReveal>
      </section>
    </div>
  );
}
